/**
 * TRAC-1049 — the open ask for a person, as every tab of the conversation can see it.
 *
 * The chat has two records of a handoff and they are scoped differently:
 *
 *   - `traci_handoff_attempt` (handoffAttempt.ts) is per TAB — `sessionStorage`. It is what
 *     the NEXT PAGE of the same tab reads before its first render, which is what TRAC-1006
 *     needed. A second tab of the same conversation never has one.
 *   - this record, `traci_handoff_funnel`, is per ORIGIN — `localStorage`, like the transcript
 *     and the session id. The funnel writes it so a wait that crosses pages is still measured
 *     from the press, and it is the ONLY record of the open ask that a second tab can read.
 *
 * So everything a tab that did not press the button has to know — that an ask is open, which
 * team it is for, and whether anyone has been told we are still trying — belongs here. It was
 * already holding all three; nothing read it for the UI, which is the whole of TRAC-1049.
 *
 * Pure: storage and the key are passed in. handoffFunnel binds them to `localStorage`;
 * RealChatAgentService reads the same record to decide whether it is still worth asking the
 * backend about a handoff (it cannot import the funnel — the funnel imports it).
 *
 * Extending it: add an optional field, read it defensively below, and keep old records
 * readable. A record this code cannot read is treated as absent, which is the behaviour
 * before this file existed — never worse than before.
 */

export interface OpenHandoffAttempt {
  /** The conversation this ask belongs to. Another session never inherits it. */
  sessionId: string;
  /** Epoch ms of the press. Epoch, not performance.now(): it has to survive a page change. */
  askedAt: number;
  /**
   * The team this ask is for: the customer's pick at first, replaced by the team the backend
   * actually routed to once it says. Undefined when nobody has named one (the classifier path,
   * where the customer typed their question instead of picking a door).
   */
  department?: string;
  /**
   * TRAC-1049: true once the BACKEND has echoed the team, not merely when the customer picked
   * one. Only a confirmed team may be named on screen — the intended team can be closed or
   * busy and the chat rerouted, and a heading that named it would be a lie the customer reads.
   * The tab that asked draws a generic heading until this flips; every other tab must do the
   * same, or two tabs of one conversation name two different teams.
   */
  departmentConfirmed?: boolean;
  /** Epoch ms at which some tab showed the "still trying" message for this ask. */
  slowAt?: number;
}

/** Shared with every tab on the origin, unlike the per-tab `traci_handoff_attempt`. */
export const OPEN_ATTEMPT_STORAGE_KEY = 'traci_handoff_funnel';

/** Nobody is still waiting for a person a day later; a record that old is a stale tab. */
export const OPEN_ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;

export function readOpenAttempt(
  storage: Storage | null,
  key: string,
  sessionId: string | null,
  now: number = Date.now(),
): OpenHandoffAttempt | null {
  if (!storage || !sessionId) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const rec = JSON.parse(raw) as Partial<OpenHandoffAttempt>;
    if (rec.sessionId !== sessionId || typeof rec.askedAt !== 'number') return null;
    if (now - rec.askedAt > OPEN_ATTEMPT_TTL_MS) return null;
    return {
      sessionId: rec.sessionId,
      askedAt: rec.askedAt,
      department: typeof rec.department === 'string' && rec.department ? rec.department : undefined,
      departmentConfirmed: rec.departmentConfirmed === true,
      slowAt: typeof rec.slowAt === 'number' ? rec.slowAt : undefined,
    };
  } catch {
    return null; // blocked storage or a corrupt record: behave as if nothing was saved
  }
}

export function writeOpenAttempt(storage: Storage | null, key: string, rec: OpenHandoffAttempt): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(rec));
  } catch {
    /* storage may be unavailable; the page-local state still works */
  }
}

export function clearOpenAttempt(storage: Storage | null, key: string): void {
  if (!storage) return;
  try { storage.removeItem(key); } catch { /* as above */ }
}
